/**
 * XAI Chat Feedback Prompt Component
 * 
 * Displays feedback prompt for Polaris Chat (XAI Chat) responses.
 * Adapted from ChatFeedbackPrompt.tsx for XAI chat endpoint.
 * 
 * Submits to: POST /api/xai-chat/feedback/
 */

import React, { useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { ThumbUp, ThumbDown } from "@mui/icons-material";
import { apiUrl } from "@app/const";
import { getAccessToken } from "@app/api/getAccessToken";
import { store } from "@app/redux/store";

export interface XAIChatFeedbackPromptProps {
  planResultRootId: string;
  planResultTypeId: string;
  requestId: string;
  query?: string;
  response?: string;
  onSubmitted?: () => void;
}

export const XAIChatFeedbackPrompt: React.FC<XAIChatFeedbackPromptProps> = ({
  planResultRootId,
  planResultTypeId,
  requestId,
  query,
  response,
  onSubmitted,
}) => {
  const [isSatisfied, setIsSatisfied] = useState<boolean | null>(null);
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleFeedback = async (satisfied: boolean) => {
    if (isSubmitted) return;

    setIsSatisfied(satisfied);
  };

  const handleSubmit = async (satisfied?: boolean) => {
    if (isSubmitting || isSubmitted) return;

    const feedbackValue = satisfied !== undefined ? satisfied : isSatisfied;
    if (feedbackValue === null) return;

    setIsSubmitting(true);

    try {
      const accessToken = await getAccessToken(store);
      if (!accessToken) {
        throw new Error("No access token available");
      }

      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const url = `${baseUrl}/api/xai-chat/feedback/`;

      const fetchResponse = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          request_id: requestId,
          is_satisfied: feedbackValue,
          comments: comments.trim() || undefined,
          query: query || undefined,
          response: response || undefined,
          plan_result_root_id: planResultRootId,
          plan_result_type_id: planResultTypeId,
        }),
      });

      if (!fetchResponse.ok) {
        throw new Error(`Failed to submit feedback: ${fetchResponse.status}`);
      }

      setIsSubmitted(true);
      onSubmitted?.();
      console.log("[XAI Chat] Feedback submitted successfully:", {
        requestId,
        isSatisfied: feedbackValue,
      });
    } catch (error) {
      console.error("[XAI Chat] Failed to submit feedback:", error);
      // Don't show error to user - just log it
      // The feedback prompt will remain visible so they can try again
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          bgcolor: "success.light",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: "success.dark" }}>
          Thank you for your feedback!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: "grey.100",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="body2"
        sx={{ mb: 1.5, fontWeight: 500, color: "primary.main" }}
      >
        Was this response helpful?
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: isSatisfied !== null ? 1.5 : 0 }}>
        <Button
          variant={isSatisfied === true ? "contained" : "outlined"}
          size="small"
          startIcon={<ThumbUp />}
          onClick={() => handleFeedback(true)}
          disabled={isSubmitting || isSubmitted}
          sx={{
            flex: 1,
            textTransform: "none",
          }}
        >
          Yes
        </Button>
        <Button
          variant={isSatisfied === false ? "contained" : "outlined"}
          color={isSatisfied === false ? "error" : "primary"}
          size="small"
          startIcon={<ThumbDown />}
          onClick={() => handleFeedback(false)}
          disabled={isSubmitting || isSubmitted}
          sx={{
            flex: 1,
            textTransform: "none",
          }}
        >
          No
        </Button>
      </Box>

      {isSatisfied !== null && (
        <Box sx={{ mt: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder={
              isSatisfied
                ? "What worked well? (optional)"
                : "What could we improve? (optional)"
            }
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={isSubmitting || isSubmitted}
            sx={{ mb: 1 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => handleSubmit()}
            disabled={isSubmitting || isSubmitted}
            sx={{ textTransform: "none" }}
          >
            {isSubmitting ? "Submitting..." : "Submit Feedback"}
          </Button>
        </Box>
      )}
    </Box>
  );
};
